import React, { useState, useEffect, useContext } from 'react';
import { Users, Plus, Search, TrendingUp, FileText, MapPin, Phone, Mail, Calendar, Filter, Edit3, Trash2, Eye, Drill, Loader, AlertCircle, Receipt, Wrench } from 'lucide-react';

// Import API functions
import {
    fetchAllAgents,
    createAgent,
    updateAgent,
    deleteAgent,
    fetchAllDrillingDataEntries,
    fetchDrillingDataStats,
    createBill,
    addBillToAgent,
    addDrillToAgent,

} from '../../../services/api'; // Adjust the import path as needed
import { AuthContext } from '../../../context/AuthContext';
import AgentDrillingForm from './Forms/AgentDrillingForm';
import { Link } from 'react-router-dom';

const AgentComponent = () => {

    // Get vehicles data and functions from context
    const {
        getAllVehicles,
        allVehicles,
        allVehiclesLoading,
        allVehiclesError,

        getAllAgents,
        allAgents,
        allAgentsLoading,
        allAgentsError,

    } = useContext(AuthContext);

    const [activeTab, setActiveTab] = useState('dashboard');



    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingAgent, setEditingAgent] = useState(null);
    const [formLoading, setFormLoading] = useState(false);
    const [showBillForm, setShowBillForm] = useState(false);
    const [showDrillingForm, setShowDrillingForm] = useState(false);
    const [selectedAgentForAction, setSelectedAgentForAction] = useState(null);
    const [newAgent, setNewAgent] = useState({
        name: '',
        number: '',
        address: '',
        status: 'active'
    });

    const [drillStats, setDrillStats] = useState({
        totalEntries: 0,
        vehicleStats: [],
        customerTypeStats: [],
        recentEntries: []
    });

    // Bill form state
    const [newBill, setNewBill] = useState({
        clientName: '',
        items: [{ name: '', quantity: 1, rate: 0, total: 0 }]
    });

    // Drilling data form state
    const [newDrillingData, setNewDrillingData] = useState({
        date: new Date().toISOString().split('T')[0],
        vehicleName: '',
        typeOfCustomer: 'Agent',
        name: '',
        address: '',
        mobileNumber: '',
        drillFeet: '',
        additional: [{ name: '', quantity: '' }]
    });

    // Stats state
    const [stats, setStats] = useState({
        totalAgents: 0,
        activeAgents: 0,
        totalProjects: 0,
        totalAmount: 0,
        totalBills: 0
    });


    useEffect(() => {
        loadAgents();
        loadStats();

        if (!allVehicles || allVehicles.length === 0) {
            getAllVehicles();
        }
    }, [allAgents, allVehicles]); // Add dependencies



    const loadAgents = async () => {
        try {
            setLoading(true);
            setError(null);

            // Use context function instead of direct API call
            if (!allAgents || allAgents.length === 0) {
                await getAllAgents();
            }

            // Process the context data
            const processedAgents = allAgents.map((agent, index) => ({
                ...agent,
                id: agent._id || agent.id,
                drillingProjects: agent.DrillingPoint?.length || 0,
                totalBills: agent.Bill?.length || 0,
                totalAmount: calculateTotalAmount(agent.Bill || []),
                status: agent.status || 'active',
                avatar: getRandomAvatar(index)
            }));

            setAgents(processedAgents);
            calculateStats(processedAgents);
        } catch (err) {
            setError(err.message || 'Failed to load agents');
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const { data } = await fetchDrillingDataStats();
            setDrillStats(data);
        } catch (err) {
            console.warn('Could not load drilling stats:', err);
        }
    };

    const calculateTotalAmount = (bills) => {
        if (!Array.isArray(bills)) return 0;
        return bills.reduce((total, bill) => {
            if (bill.item && Array.isArray(bill.item)) {
                return total + bill.item.reduce((sum, item) => sum + (item.total || 0), 0);
            }
            return total;
        }, 0);
    };

    const getRandomAvatar = (index) => {
        const avatars = ['🧑‍💼', '👩‍💼', '👨‍🔧', '👩‍💻', '👨‍💻', '👷‍♂️', '👷‍♀️', '🏗️'];
        return avatars[index % avatars.length];
    };

    const calculateStats = (agentsData) => {
        const totalAgents = agentsData.length;
        const activeAgents = agentsData.filter(a => a.status === 'active').length;
        const totalProjects = agentsData.reduce((sum, agent) => sum + agent.drillingProjects, 0);
        const totalAmount = agentsData.reduce((sum, agent) => sum + agent.totalAmount, 0);
        const totalBills = agentsData.reduce((sum, agent) => sum + agent.totalBills, 0);

        setStats({
            totalAgents,
            activeAgents,
            totalProjects,
            totalAmount,
            totalBills
        });
    };

    const filteredAgents = agents.filter(agent => {
        const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            agent.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
            agent.address.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesSearch;
    });

    const handleAddAgent = async () => {
        if (newAgent.name && newAgent.number && newAgent.address) {
            try {
                setFormLoading(true);
                const response = await createAgent(newAgent);

                // Handle different response structures
                const createdAgent = response.data || response.agent || response;

                // Refresh context data instead of managing local state
                await getAllAgents();


                const processedAgent = {
                    ...createdAgent,
                    id: createdAgent._id || createdAgent.id,
                    drillingProjects: 0,
                    totalBills: 0,
                    totalAmount: 0,
                    avatar: getRandomAvatar(agents.length)
                };

                setAgents([...agents, processedAgent]);
                calculateStats([...agents, processedAgent]);

                setNewAgent({
                    name: '',
                    number: '',
                    address: '',
                    status: 'active'
                });
                setShowAddForm(false);
                setError(null);
            } catch (err) {
                setError(err.message || 'Failed to create agent');
                console.error('Error creating agent:', err);
            } finally {
                setFormLoading(false);
            }
        }
    };

    const handleEditAgent = (agent) => {
        setEditingAgent(agent);
        setNewAgent({
            name: agent.name,
            number: agent.number,
            address: agent.address,
            status: agent.status
        });
        setShowAddForm(true);
    };

    const handleUpdateAgent = async () => {
        if (!editingAgent) return;

        try {
            setFormLoading(true);
            const response = await updateAgent(editingAgent.id, newAgent);

            // Handle different response structures
            const updatedAgent = response.data || response.agent || response;

            const processedAgent = {
                ...editingAgent,
                ...updatedAgent,
                id: updatedAgent._id || updatedAgent.id || editingAgent.id
            };

            const updatedAgents = agents.map(agent =>
                agent.id === editingAgent.id ? processedAgent : agent
            );

            setAgents(updatedAgents);
            calculateStats(updatedAgents);

            setEditingAgent(null);
            setNewAgent({
                name: '',
                number: '',
                address: '',
                status: 'active'
            });
            setShowAddForm(false);
            setError(null);
        } catch (err) {
            setError(err.message || 'Failed to update agent');
            console.error('Error updating agent:', err);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDeleteAgent = async (id) => {
        if (window.confirm('Are you sure you want to remove this agent?')) {
            try {
                setLoading(true);
                await deleteAgent(id);

                const updatedAgents = agents.filter(agent => agent.id !== id);
                setAgents(updatedAgents);
                calculateStats(updatedAgents);
                setError(null);
            } catch (err) {
                setError(err.message || 'Failed to delete agent');
                console.error('Error deleting agent:', err);
            } finally {
                setLoading(false);
            }
        }
    };

    const handleAddBill = (agent) => {
        setSelectedAgentForAction(agent);
        setShowBillForm(true);
    };

    const handleAddDrillingData = (agent) => {
        setSelectedAgentForAction(agent);
        setShowDrillingForm(true);
    };

    const handleSubmitBill = async (id) => {
        if (!selectedAgentForAction || !newBill.clientName) return;


        try {
            setFormLoading(true);
            // Calculate totals for items
            const itemsWithTotals = newBill.items.map(item => ({
                ...item,
                total: item.quantity * item.rate
            }));

            const billData = {
                ...newBill,
                item: itemsWithTotals
            };

            // Here you would call your createBill API
            const response = await addBillToAgent(selectedAgentForAction._id, billData);
            // Then link it to the agent

            console.log(response, "responseresponseresponse")

            console.log('Creating bill for agent:', selectedAgentForAction.id, billData);

            // For now, just update the UI to show success
            setNewBill({
                clientName: '',
                items: [{ name: '', quantity: 1, rate: 0, total: 0 }]
            });
            setShowBillForm(false);
            setSelectedAgentForAction(null);
            setError(null);

            // Refresh agents to show updated data
            await loadAgents();
        } catch (err) {
            setError(err.message || 'Failed to create bill');
            console.error('Error creating bill:', err);
        } finally {
            setFormLoading(false);
        }
    };

    const handleSubmitDrillingData = async () => {
        if (!selectedAgentForAction || !newDrillingData.vehicleName) return;

        try {
            setFormLoading(true);

            // Here you would call your createDrillingDataEntry API
            const response = await addDrillToAgent(selectedAgentForAction._id, newDrillingData);
            // Then link it to the agent

            console.log('Creating drilling data for agent:', selectedAgentForAction.id, newDrillingData);

            // For now, just update the UI to show success
            setNewDrillingData({
                date: new Date().toISOString().split('T')[0],
                vehicleName: '',
                typeOfCustomer: 'Agent',
                name: '',
                address: '',
                mobileNumber: '',
                drillFeet: '',
                additional: [{ name: '', quantity: '' }]
            });
            setShowDrillingForm(false);
            setSelectedAgentForAction(null);
            setError(null);

            // Refresh agents to show updated data
            await loadAgents();
        } catch (err) {
            setError(err.message || 'Failed to create drilling data entry');
            console.error('Error creating drilling data entry:', err);
        } finally {
            setFormLoading(false);
        }
    };

    const addBillItem = () => {
        setNewBill({
            ...newBill,
            items: [...newBill.items, { name: '', quantity: 1, rate: 0, total: 0 }]
        });
    };

    const removeBillItem = (index) => {
        const updatedItems = newBill.items.filter((_, i) => i !== index);
        setNewBill({ ...newBill, items: updatedItems });
    };

    const updateBillItem = (index, field, value) => {
        const updatedItems = newBill.items.map((item, i) => {
            if (i === index) {
                const updatedItem = { ...item, [field]: value };
                if (field === 'quantity' || field === 'rate') {
                    updatedItem.total = updatedItem.quantity * updatedItem.rate;
                }
                return updatedItem;
            }
            return item;
        });
        setNewBill({ ...newBill, items: updatedItems });
    };

    const addAdditionalItem = () => {
        setNewDrillingData({
            ...newDrillingData,
            additional: [...newDrillingData.additional, { name: '', quantity: '' }]
        });
    };

    const removeAdditionalItem = (index) => {
        const updatedAdditional = newDrillingData.additional.filter((_, i) => i !== index);
        setNewDrillingData({ ...newDrillingData, additional: updatedAdditional });
    };

    const updateAdditionalItem = (index, field, value) => {
        const updatedAdditional = newDrillingData.additional.map((item, i) =>
            i === index ? { ...item, [field]: value } : item
        );
        setNewDrillingData({ ...newDrillingData, additional: updatedAdditional });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const StatCard = ({ title, value, icon: Icon, color, subtitle }) => (
        <div className="bg-white rounded-xl shadow-lg p-6 border-l-4" style={{ borderLeftColor: color }}>
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-600 text-sm font-medium">{title}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
                    {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
                </div>
                <div className="p-3 rounded-full" style={{ backgroundColor: color + '20' }}>
                    <Icon size={24} style={{ color }} />
                </div>
            </div>
        </div>
    );

    const AgentCard = ({ agent }) => (
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow border border-gray-100">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                    <div className="text-3xl">{agent.avatar}</div>
                    <div>
                        <h3 className="font-bold text-gray-900 text-lg">{agent.name}</h3>
                        <p className="text-blue-600 font-medium"><Link to={agent.id}>Agent ID: {agent.id}</Link></p>
                    </div>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => handleEditAgent(agent)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        disabled={loading}
                        title="Edit Agent"
                    >
                        <Edit3 size={16} />
                    </button>
                    <button
                        onClick={() => handleDeleteAgent(agent.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        disabled={loading}
                        title="Delete Agent"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>

            <div className="space-y-3 mb-4">
                <div className="flex items-start text-gray-600">
                    <MapPin size={16} className="mr-2 mt-1 flex-shrink-0" />
                    <span className="text-sm">{agent.address}</span>
                </div>
                <div className="flex items-center text-gray-600">
                    <Phone size={16} className="mr-2" />
                    <span className="text-sm">{agent.number}</span>
                </div>
                <div className="flex items-center text-gray-600">
                    <Calendar size={16} className="mr-2" />
                    <span className="text-sm">
                        Joined: {agent.createdAt ? new Date(agent.createdAt).toLocaleDateString() : 'N/A'}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100 mb-4">
                <div>
                    <p className="text-xs text-gray-500">Drilling Projects</p>
                    <p className="font-bold text-blue-600">{agent.drillingProjects}</p>
                </div>
                <div>
                    <p className="text-xs text-gray-500">Total Bills</p>
                    <p className="font-bold text-purple-600">{agent.totalBills}</p>
                </div>
                <div className="col-span-2">
                    <p className="text-xs text-gray-500">Total Amount</p>
                    <p className="font-bold text-green-600">{formatCurrency(agent.totalAmount)}</p>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-2 pt-4 border-t border-gray-100">
                
                <button
                    onClick={() => handleAddDrillingData(agent)}
                    className="flex-1 bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 flex items-center justify-center space-x-1 transition-colors text-sm"
                    disabled={loading}
                >
                    <Drill size={14} />
                    <span>Add Drilling Point</span>
                </button>
                <button
                    onClick={() => handleAddBill(agent)}
                    className="flex-1 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 flex items-center justify-center space-x-1 transition-colors text-sm"
                    disabled={loading}
                >
                    <Receipt size={14} />
                    <span>Receive Payment</span>
                </button>
            </div>
        </div>
    );

    const LoadingSpinner = () => (
        <div className="flex items-center justify-center py-12">
            <Loader className="animate-spin text-blue-600" size={32} />
            <span className="ml-2 text-gray-600">Loading...</span>
        </div>
    );

    const ErrorMessage = ({ message, onRetry }) => (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-3">
            <AlertCircle className="text-red-600" size={20} />
            <div className="flex-1">
                <p className="text-red-800 font-medium">Error</p>
                <p className="text-red-600 text-sm">{message}</p>
            </div>
            {onRetry && (
                <button
                    onClick={onRetry}
                    className="px-3 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                >
                    Retry
                </button>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b border-gray-200">
                <div className=" mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="text-2xl font-bold text-blue-900">🏗️ Agent Management</div>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={loadAgents}
                                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                                disabled={loading}
                            >
                                {loading ? <Loader size={16} className="animate-spin" /> : '🔄 Refresh'}
                            </button>
                            <div className="text-sm text-gray-600">
                                {new Date().toLocaleDateString('en-IN', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Navigation Tabs */}
            <div className="bg-white border-b border-gray-200">
                <div className=" mx-auto px-6">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'dashboard', label: 'Dashboard', icon: TrendingUp },
                            { id: 'agents', label: 'All Agents', icon: Users },
                            { id: 'projects', label: 'Projects & Bills', icon: FileText }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                    ? 'border-blue-500 text-blue-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <tab.icon size={18} />
                                <span>{tab.label}</span>
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div className=" mx-auto px-6 py-8">
                {/* Error Message */}
                {error && (
                    <div className="mb-6">
                        <ErrorMessage message={error} onRetry={loadAgents} />
                    </div>
                )}

                {/* Dashboard Tab */}
                {activeTab === 'dashboard' && (
                    <div className="space-y-8">
                        <div className="flex items-center justify-between">
                            <h1 className="text-3xl font-bold text-gray-900">Agent Dashboard</h1>
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                                disabled={loading}
                            >
                                <Plus size={20} />
                                <span>Add Agent</span>
                            </button>
                        </div>



                        {loading && <LoadingSpinner />}

                        {!loading && (
                            <>
                                {/* Stats Grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                    <StatCard
                                        title="Total Agents"
                                        value={stats.totalAgents}
                                        icon={Users}
                                        color="#3B82F6"
                                        subtitle={`${stats.activeAgents} active`}
                                    />
                                    <StatCard
                                        title="Drilling Projects"
                                        value={stats.totalProjects}
                                        icon={Drill}
                                        color="#10B981"
                                        subtitle="All projects"
                                    />
                                    <StatCard
                                        title="Total Bills"
                                        value={stats.totalBills}
                                        icon={FileText}
                                        color="#8B5CF6"
                                        subtitle="Generated bills"
                                    />
                                    <StatCard
                                        title="Total Amount"
                                        value={formatCurrency(stats.totalAmount)}
                                        icon={TrendingUp}
                                        color="#F59E0B"
                                        subtitle="From all bills"
                                    />
                                </div>

                                {/* Top Performers by Amount */}
                                <div className="bg-white rounded-xl shadow-lg p-6">
                                    <h2 className="text-xl font-bold text-gray-900 mb-6">Top Agents by Revenue</h2>
                                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                        {agents
                                            .sort((a, b) => b.totalAmount - a.totalAmount)
                                            .slice(0, 3)
                                            .map((agent, index) => (
                                                <div key={agent.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                                                    <div className="text-2xl">{agent.avatar}</div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center space-x-2">
                                                            <h3 className="font-bold text-gray-900">{agent.name}</h3>
                                                            {index === 0 && <span className="text-yellow-500">🏆</span>}
                                                            {index === 1 && <span className="text-gray-400">🥈</span>}
                                                            {index === 2 && <span className="text-orange-600">🥉</span>}
                                                        </div>
                                                        <p className="text-sm text-gray-600">{agent.drillingProjects} projects</p>
                                                        <p className="text-lg font-bold text-green-600">{formatCurrency(agent.totalAmount)}</p>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {true &&
                            // Add the following inside the Dashboard tab (after Top Agents):
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {/* Vehicle Stats */}
                                <div className="bg-white p-4 rounded-xl shadow-md">
                                    <h3 className="text-lg font-bold mb-4">Top Vehicles</h3>
                                    {drillStats.vehicleStats.slice(0, 3).map(v => (
                                        <div key={v._id} className="flex justify-between py-1 text-sm text-gray-700">
                                            <span>{v._id}</span>
                                            <span>{v.count}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Customer Type Stats */}
                                <div className="bg-white p-4 rounded-xl shadow-md">
                                    <h3 className="text-lg font-bold mb-4">Customer Types</h3>
                                    {drillStats.customerTypeStats.map(c => (
                                        <div key={c._id} className="flex justify-between py-1 text-sm text-gray-700">
                                            <span>{c._id}</span>
                                            <span>{c.count}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Recent Entries */}
                                <div className="bg-white p-4 rounded-xl shadow-md">
                                    <h3 className="text-lg font-bold mb-4">Recent Projects</h3>
                                    {drillStats.recentEntries.map(e => (
                                        <div key={e._id} className="text-sm text-gray-700 border-b py-2">
                                            <div className="font-medium">{e.name || 'Unnamed'}</div>
                                            <div className="text-xs text-gray-500">{e.vehicleName} - {e.typeOfCustomer}</div>
                                            <div className="text-xs text-gray-400">{new Date(e.createdAt).toLocaleDateString()}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>}
                    </div>
                )}

                {/* All Agents Tab */}
                {activeTab === 'agents' && (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h1 className="text-3xl font-bold text-gray-900">All Agents</h1>
                            <button
                                onClick={() => setShowAddForm(true)}
                                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 transition-colors"
                                disabled={loading}
                            >
                                <Plus size={20} />
                                <span>Add Agent</span>
                            </button>
                        </div>

                        {/* Search */}
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-3 text-gray-400" size={20} />
                                    <input
                                        type="text"
                                        placeholder="Search agents by name, phone number, or address..."
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            <>
                                {/* Agents Grid */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                                    {filteredAgents.map(agent => (
                                        <AgentCard key={agent.id} agent={agent} />
                                    ))}
                                </div>

                                {filteredAgents.length === 0 && !loading && (
                                    <div className="text-center py-12">
                                        <Users size={48} className="text-gray-300 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-gray-900 mb-2">No agents found</h3>
                                        <p className="text-gray-500">Try adjusting your search criteria</p>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {/* Projects & Bills Tab */}
                {activeTab === 'projects' && (
                    <div className="space-y-6">
                        <h1 className="text-3xl font-bold text-gray-900">Projects & Bills Overview</h1>

                        {loading ? (
                            <LoadingSpinner />
                        ) : (
                            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Agent</th>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Contact</th>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Drilling Projects</th>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Total Bills</th>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Total Amount</th>
                                                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {agents
                                                .sort((a, b) => b.totalAmount - a.totalAmount)
                                                .map(agent => (
                                                    <tr key={agent.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-3">
                                                                <span className="text-xl">{agent.avatar}</span>
                                                                <div>
                                                                    <div className="font-medium text-gray-900">{agent.name}</div>
                                                                    <div className="text-sm text-gray-500">ID: {agent.id}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="text-sm text-gray-900">{agent.number}</div>
                                                            <div className="text-sm text-gray-500 max-w-xs truncate">{agent.address}</div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm font-medium text-blue-600">
                                                            {agent.drillingProjects}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm font-medium text-purple-600">
                                                            {agent.totalBills}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm font-bold text-green-600">
                                                            {formatCurrency(agent.totalAmount)}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${agent.status === 'active'
                                                                ? 'bg-green-100 text-green-800'
                                                                : 'bg-red-100 text-red-800'
                                                                }`}>
                                                                {agent.status}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Add/Edit Agent Modal */}
            {showAddForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">
                                    {editingAgent ? 'Edit Agent' : 'Add New Agent'}
                                </h2>
                                <button
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setEditingAgent(null);
                                        setNewAgent({
                                            name: '',
                                            number: '',
                                            address: '',
                                            status: 'active'
                                        });
                                        setError(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                    disabled={formLoading}
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newAgent.name}
                                        onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })}
                                        placeholder="Enter full name"
                                        required
                                        disabled={formLoading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                                    <input
                                        type="tel"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newAgent.number}
                                        onChange={(e) => setNewAgent({ ...newAgent, number: e.target.value })}
                                        placeholder="Enter phone number"
                                        required
                                        disabled={formLoading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                                    <textarea
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        rows="3"
                                        value={newAgent.address}
                                        onChange={(e) => setNewAgent({ ...newAgent, address: e.target.value })}
                                        placeholder="Enter complete address"
                                        required
                                        disabled={formLoading}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                    <select
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newAgent.status}
                                        onChange={(e) => setNewAgent({ ...newAgent, status: e.target.value })}
                                        disabled={formLoading}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowAddForm(false);
                                        setEditingAgent(null);
                                        setNewAgent({
                                            name: '',
                                            number: '',
                                            address: '',
                                            status: 'active'
                                        });
                                        setError(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                    disabled={formLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={editingAgent ? handleUpdateAgent : handleAddAgent}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
                                    disabled={!newAgent.name || !newAgent.number || !newAgent.address || formLoading}
                                >
                                    {formLoading ? (
                                        <Loader size={16} className="animate-spin" />
                                    ) : (
                                        editingAgent ? 'Update Agent' : 'Add Agent'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Bill Modal */}
            {showBillForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">
                                    Add Bill for {selectedAgentForAction?.name}

                                </h2>
                                <button
                                    onClick={() => {
                                        setShowBillForm(false);
                                        setSelectedAgentForAction(null);
                                        setNewBill({
                                            clientName: '',
                                            items: [{ name: '', quantity: 1, rate: 0, total: 0 }]
                                        });
                                        setError(null);
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                    disabled={formLoading}
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Client Name *</label>
                                    <input
                                        type="text"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                        value={newBill.clientName}
                                        onChange={(e) => setNewBill({ ...newBill, clientName: e.target.value })}
                                        placeholder="Enter client name"
                                        required
                                        disabled={formLoading}
                                    />
                                </div>

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="block text-sm font-medium text-gray-700">Items</label>
                                        <button
                                            onClick={addBillItem}
                                            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                                            disabled={formLoading}
                                        >
                                            Add Item
                                        </button>
                                    </div>

                                    <div className="space-y-3">
                                        {newBill.items.map((item, index) => (
                                            <div key={index} className="grid grid-cols-12 gap-2 items-end">
                                                <div className="col-span-4">
                                                    <label className="block text-xs text-gray-500 mb-1">Item Name</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.name}
                                                        onChange={(e) => updateBillItem(index, 'name', e.target.value)}
                                                        placeholder="Item name"
                                                        disabled={formLoading}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs text-gray-500 mb-1">Qty</label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.quantity}
                                                        onChange={(e) => updateBillItem(index, 'quantity', parseInt(e.target.value) || 1)}
                                                        disabled={formLoading}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <label className="block text-xs text-gray-500 mb-1">Rate (₹)</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                                        value={item.rate}
                                                        onChange={(e) => updateBillItem(index, 'rate', parseFloat(e.target.value) || 0)}
                                                        disabled={formLoading}
                                                    />
                                                </div>
                                                <div className="col-span-3">
                                                    <label className="block text-xs text-gray-500 mb-1">Total (₹)</label>
                                                    <input
                                                        type="text"
                                                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50"
                                                        value={formatCurrency(item.total)}
                                                        disabled
                                                    />
                                                </div>
                                                <div className="col-span-1">
                                                    {newBill.items.length > 1 && (
                                                        <button
                                                            onClick={() => removeBillItem(index)}
                                                            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                                                            disabled={formLoading}
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                        <div className="flex justify-between items-center">
                                            <span className="font-medium text-gray-700">Total Amount:</span>
                                            <span className="text-lg font-bold text-green-600">
                                                {formatCurrency(newBill.items.reduce((sum, item) => sum + item.total, 0))}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-3 mt-6">
                                <button
                                    onClick={() => {
                                        setShowBillForm(false);
                                        setSelectedAgentForAction(null);
                                        setNewBill({
                                            clientName: '',
                                            items: [{ name: '', quantity: 1, rate: 0, total: 0 }]
                                        });
                                        setError(null);
                                    }}
                                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                    disabled={formLoading}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => handleSubmitBill(selectedAgentForAction.id)}
                                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center"
                                    disabled={!newBill.clientName || newBill.items.length === 0 || formLoading}
                                >
                                    {formLoading ? (
                                        <Loader size={16} className="animate-spin" />
                                    ) : (
                                        'Create Bill'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Drilling Data Modal */}
            {showDrillingForm && (
                <AgentDrillingForm selectedAgentForAction={selectedAgentForAction} showDrillingForm={showDrillingForm} setShowDrillingForm={setShowDrillingForm}/>
            )}
        </div>
    );
};

export default AgentComponent;